import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators';

import { CarbonFootprint } from '../../../models/carbon-footprint.model';
import { COMPANY_ACTIVITIES, Company } from '../../../models/company.model';
import { CarbonFootprintService } from '../../../services/carbon-footprint.service';
import { CompanyService } from '../../../services/company.service';

/**
 * Badge "Huella de carbono" con un popover que detalla, por especie, las hectáreas
 * medidas y las toneladas sustentables del productor en la campaña activa.
 *
 * Los datos vienen de huella-back vía connect-back ([[CarbonFootprintService]]); el
 * badge solo se muestra si el productor tiene al menos una especie medida.
 *
 * ## Usage
 * ``` html
 * <carbon-footprint-icon [company]="company"></carbon-footprint-icon>
 * ```
 */
@Component({
  selector: 'carbon-footprint-icon',
  templateUrl: './carbon-footprint-icon.component.html',
  styleUrls: ['./carbon-footprint-icon.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CarbonFootprintIconComponent implements OnInit {

  @Input() public set company(c: Company) {
    this.company$.next(c);
  }

  public data$: Observable<CarbonFootprint | null>;
  /** The language currently used. */
  public currentLang: string;

  private company$ = new BehaviorSubject<Company>(null);

  constructor(
    private carbonFootprintService: CarbonFootprintService,
    private companyService: CompanyService,
    private translateService: TranslateService
  ) { }

  /** @ignore */
  ngOnInit(): void {
    this.currentLang = this.translateService.currentLang === 'es' ? undefined : this.translateService.currentLang;

    const myCompanyId$ = this.companyService.watch().pipe(
      filter(myCompany => !!myCompany),
      map(myCompany => myCompany.id),
      distinctUntilChanged()
    );

    const targetCompany$ = this.company$.pipe(
      distinctUntilChanged((a, b) => a?.id === b?.id)
    );

    this.data$ = combineLatest([myCompanyId$, targetCompany$]).pipe(
      switchMap(([myCompanyId, company]) => {
        if (!company?.id || !this.mayBeFarmer(company)) return of(null);

        return this.carbonFootprintService.get(myCompanyId, company.id);
      })
    );
  }

  /**
   * Solo los productores miden huella de carbono. `company.activity` viene null en
   * algunos payloads (ej: negociaciones); en ese caso se consulta igual y el back,
   * que sí conoce la actividad, decide.
   */
  private mayBeFarmer(company: Company): boolean {
    const activityId = company.activity?.id;
    return activityId == null || COMPANY_ACTIVITIES.FARMER.includes(activityId);
  }
}
